import { cn } from '@/lib/utils';
import React from 'react';

type AlertVariant = 'info' | 'warning' | 'error';

interface AlertProps {
  variant?: AlertVariant;
  children: React.ReactNode;
  className?: string;
  id?: string;
}

const variantStyles = {
  info: {
    light: {
      container:
        'bg-blue-500 border-blue-500',
      icon: 'text-white',
      text: 'text-white'
    },
    dark: {
      container: 'bg-blue-500 border-blue-500',
      icon: 'text-white',
      text: 'text-white'
    }
  },
  warning: {
    light: {
      container: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-900',
      icon: 'text-yellow-400 dark:text-yellow-300',
      text: 'text-yellow-700 dark:text-yellow-200'
    },
    dark: {
      container: 'bg-yellow-950 border-yellow-900',
      icon: 'text-yellow-300',
      text: 'text-yellow-200'
    }
  },
  error: {
    light: {
      container: 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-900',
      icon: 'text-red-400 dark:text-red-300',
      text: 'text-red-700 dark:text-red-200'
    },
    dark: {
      container: 'bg-red-950 border-red-900',
      icon: 'text-red-300',
      text: 'text-red-200'
    }
  }
};

const icons = {
  info: (
    <svg
      className="h-5 w-5"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
        clipRule="evenodd"
      />
    </svg>
  ),
  warning: (
    <svg
      className="h-5 w-5"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg
      className="h-5 w-5"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  children,
  className,
  id
}) => {
  const styles = variantStyles[variant].light;

  return (
    <div
      id={id}
      className={cn(
        'border rounded-lg mb-0 alert relative overflow-hidden',
        'p-4',
        styles.container,
        className
      )}
    >
      <div className="flex items-center relative z-10">
        <div className={cn('flex-shrink-0', styles.icon)}>{icons[variant]}</div>
        <div className="ml-3">
          <p className={cn('text-sm font-normal', styles.text)}>{children}</p>
        </div>
      </div>
    </div>
  );
};

export default Alert;
